import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Collection } from '../../../../collection'
import * as ecModels from '../../../../models'
import type { Plugin } from '../plugin'

/**
 * When an edit is made, if we need to set it as a current entity, this will do
 * so. It will also unset it as the current entity when the edit is complete
 */
export const setCurrentEdit = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
  ) =>
    feature.actions.edit.observe().pipe(
      rxjs.mergeMap(action => {
        const { id, makeCurrent } = action.data
        if (makeCurrent === false) {
          return rxjs.EMPTY
        }
        // If a target is given, we use that. Otherwise, we use the default
        const target =
          makeCurrent === true ? ecModels.Target.DEFAULT : makeCurrent.target
        return collection.plugins.currentEntityPlugin.feature.selectors
          .getId({ target })
          .asObservable.pipe(
            rxjs.take(1),
            rxjs.map(currentId => {
              if (id === currentId) {
                // If this is already the current ID, we don't have to do
                // anything

                // NOTE: this is intended to handle two cases: (1) sequential
                // edits on the same entity, in which case we only want to mark
                // that entity as current after the first edit; and (2) an
                // entity is already the current entity when we then start
                // editing, in which case we neither need to set it as the
                // current entity, nor do we want to unset it as the current
                // entity when we are done editing
                return store.effects.SKIP
              }
              // Otherwise, sets the current entity ID
              return collection.plugins.currentEntityPlugin.storage.actions.putInIdLookup
                .from(action)
                .create({ key: target, value: id })
            }),
          )
      }),
    ),
)
